import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Table, Form, Button } from 'react-bootstrap'
import Message from './Message'
import { getreturnsReceipt, returnItems } from '../actions/posActions.js'
import { RETURN_ITEMS_RESET } from '../constants/posConstants.js'
import { formatCurrency } from '../utils/cart.js'
import {
  returnableQty,
  clampReturnQty,
  refundPreview,
  buildReturnItems
} from '../utils/returns.js'

const receiptNote = (receipt) => {
  if (receipt?.status === 'sent')
    return `A revised receipt (${receipt.barcode}) was sent to the customer.`
  if (receipt?.status === 'failed')
    return `The revised receipt could not be sent: ${receipt.reason}`
  if (receipt?.reason === 'No customer QR id on file')
    return 'No customer QR on file, so no revised receipt was sent.'
  return ''
}

const ReturnItemsPanel = ({ sale }) => {
  const dispatch = useDispatch()
  const [picks, setPicks] = useState({})

  const { loading, error, success, result } = useSelector(
    (state) => state.returnItems
  )

  const preview = useMemo(() => refundPreview(sale, picks), [sale, picks])
  const items = buildReturnItems(picks)
  const canReturn = items.length > 0 && !loading

  useEffect(() => {
    if (success) {
      setPicks({})
      // Show the revised receipt (new barcode) when one was issued
      dispatch(getreturnsReceipt(result?.receipt?.barcode || sale.barcode))
    }
  }, [dispatch, success, result, sale.barcode])

  useEffect(() => () => dispatch({ type: RETURN_ITEMS_RESET }), [dispatch])

  const submit = (e) => {
    e.preventDefault()

    if (
      window.confirm(
        `Return ${items.length} item line(s) and refund ${formatCurrency(preview.total)}?`
      )
    ) {
      dispatch(returnItems(sale.barcode, items))
    }
  }

  return (
    <Form onSubmit={submit}>
      {success && result && (
        <Message variant='success'>
          Return accepted. Refund {formatCurrency(result.refund.total)} (
          {formatCurrency(result.refund.subtotal)} +{' '}
          {formatCurrency(result.refund.taxes)} tax). Stock restored.{' '}
          {receiptNote(result.receipt)}
        </Message>
      )}
      {error && <Message variant='danger'>{error}</Message>}

      <Table striped bordered size='sm'>
        <thead>
          <tr>
            <th>Item</th>
            <th>Sold</th>
            <th>Returned</th>
            <th>Return now</th>
          </tr>
        </thead>
        <tbody>
          {sale.items.map((line) => {
            const max = returnableQty(line)

            return (
              <tr key={line.inventory}>
                <td>
                  {line.manufacturer} {line.subname}
                  <br />
                  <small>
                    {line.item} · {line.upc} · {formatCurrency(line.discprice)}{' '}
                    ea
                  </small>
                </td>
                <td>{line.quantity}</td>
                <td>{line.returnedQty || 0}</td>
                <td style={{ width: '6rem' }}>
                  <Form.Control
                    type='number'
                    size='sm'
                    min={0}
                    max={max}
                    disabled={max === 0}
                    value={picks[line.inventory] || 0}
                    onChange={(e) =>
                      setPicks((prev) => ({
                        ...prev,
                        [line.inventory]: clampReturnQty(e.target.value, line)
                      }))
                    }
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>

      <div className='mb-2'>
        Refund: <strong>{formatCurrency(preview.total)}</strong> (
        {formatCurrency(preview.subtotal)} + {formatCurrency(preview.taxes)}{' '}
        tax). Shipping is not refunded.
      </div>
      <Button type='submit' variant='outline-secondary' disabled={!canReturn}>
        Accept Return &amp; Restock
      </Button>
    </Form>
  )
}

export default ReturnItemsPanel
